import React, { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, formatDateTime } from "@/lib/api";
import { ReceiveGroup } from "@/pages/star/CashHandover";
import { ClearOfficeCash, ClearanceHistory } from "@/pages/admin/ClearOfficeCash";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const STAGE = { teacher: "Teacher", head: "Head teacher", office: "Office" };

const Stat = ({ label, value, cls, testid }) => (
  <div className={`rounded-2xl p-4 ${cls}`} data-testid={testid}><p className="text-xs font-semibold uppercase tracking-wide opacity-80">{label}</p><p className="text-2xl font-extrabold">{inr(value)}</p></div>
);

export default function CashTally() {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/cash/admin/tally").then(({ data }) => setD(data)).catch(() => toast.error("Could not load tally")), []);
  useEffect(() => { load(); }, [load]);
  const setHead = async (v) => {
    try { await api.put("/cash/admin/head-teacher", { teacher_id: v === "none" ? null : v }); toast.success("Head teacher saved"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };
  if (!d) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-blue-900" /></div>;
  const headName = d.head?.name || d.head?.email;
  const headGroup = d.head_entries.length ? { teacher_id: "head", teacher_name: headName || "Head teacher", total: d.totals.head, entries: d.head_entries } : null;
  return (
    <div className="space-y-6" data-testid="cash-tally">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-semibold text-slate-700">Head teacher:</span>
        <Select value={d.head?.id || "none"} onValueChange={setHead}>
          <SelectTrigger className="w-60" data-testid="cash-head-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Not set</SelectItem>
            {d.teachers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name || t.email}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-xs text-slate-500">Only cash entries made from now on are tracked.</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Stat label="With teachers" value={d.totals.teacher} cls="bg-amber-50 text-amber-900 border border-amber-200" testid="cash-total-teacher" />
        <Stat label={`With ${headName || "head teacher"}`} value={d.totals.head} cls="bg-sky-50 text-sky-900 border border-sky-200" testid="cash-total-head" />
        <div className="space-y-2">
          <Stat label="With office" value={d.totals.office} cls="bg-green-50 text-green-900 border border-green-200" testid="cash-total-office" />
          <div className="grid grid-cols-3 gap-1 text-[11px] text-slate-600" data-testid="cash-cleared-totals">
            <span data-testid="cash-cleared-bank">Bank <b>{inr(d.cleared?.bank)}</b></span>
            <span data-testid="cash-cleared-kept">Kept <b>{inr(d.cleared?.kept)}</b></span>
            <span data-testid="cash-cleared-used">Used <b>{inr(d.cleared?.used)}</b></span>
          </div>
        </div>
      </div>

      <section className="space-y-3">
        <h4 className="font-bold text-slate-900">Clear office cash</h4>
        <ClearOfficeCash key={d.totals.office} officeTotal={d.totals.office} onDone={load} />
      </section>

      <section className="space-y-3">
        <h4 className="font-bold text-slate-900">Receive from {headName || "head teacher"}</h4>
        {headGroup ? <ReceiveGroup key={`head-${headGroup.entries.length}`} g={headGroup} endpoint="/cash/admin/receive" extra={{ source: "head" }} onDone={load} testid="cash-admin-head" />
          : <p className="text-sm text-slate-500">Nothing waiting with the head teacher.</p>}
      </section>

      <section className="space-y-3">
        <h4 className="font-bold text-slate-900">Still with teachers</h4>
        <p className="text-xs text-slate-500">Normally the head teacher collects these. Use "Confirm received" here only when you take cash directly from a teacher.</p>
        {d.with_teachers.length === 0 ? <p className="text-sm text-slate-500">No cash with teachers.</p>
          : d.with_teachers.map((g) => <ReceiveGroup key={`${g.teacher_id}-${g.entries.length}`} g={g} endpoint="/cash/admin/receive" extra={{ source: "teacher", teacher_id: g.teacher_id }} onDone={load} testid={`cash-admin-teacher-${g.teacher_id}`} />)}
      </section>

      <section className="space-y-2">
        <h4 className="font-bold text-slate-900">Recent handovers</h4>
        {d.handovers.length === 0 ? <p className="text-sm text-slate-500">No handovers yet.</p> : (
          <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100" data-testid="cash-handovers">
            {d.handovers.map((h) => (
              <div key={h.id} className={`p-3 text-sm flex flex-wrap items-center justify-between gap-2 ${h.diff ? "bg-red-50" : ""}`} data-testid={`cash-handover-${h.id}`}>
                <span><b>{h.from_name}</b> → <b>{h.to_name}</b> · {h.count} entr{h.count === 1 ? "y" : "ies"} · <span className="text-xs text-slate-500">{formatDateTime(h.created_at)}</span></span>
                <span>Expected {inr(h.expected)} · Received <b>{inr(h.received)}</b>{h.diff ? <span className="ml-1 font-semibold text-red-600" data-testid={`cash-handover-diff-${h.id}`}>({h.diff > 0 ? "+" : ""}{inr(h.diff)} · {h.reason})</span> : " ✅"}</span>
              </div>
            ))}
          </div>
        )}
        <h4 className="font-bold text-slate-900 pt-2">Office cash cleared</h4>
        <ClearanceHistory rows={d.clearances || []} />
        <p className="text-[11px] text-slate-400">Stages: {Object.values(STAGE).join(" → ")}. Approving payments, receipts and ledgers work as before.</p>
      </section>
    </div>
  );
}
